import { z } from 'zod'
import { ajouterJours, ajouterMois, type CalendrierOuvre } from './calendrier'
import { SENS_PAIEMENT, type StatutEcheance, TYPES_PIECE, type TypePiece } from './domaine'
import { calculerEcheances, type Regle } from './generateur'
import { normaliserLibelle } from './libelle'
import { formaterMontant } from './montant'

// Vérifications : une règle qui, à chaque période, constate seule qu'une chose attendue est
// arrivée (« je reçois chaque mois le remboursement de X ») : un mouvement du relevé ou une
// pièce reçue. Trouvée, l'échéance est faite, avec sa preuve ; absente une fois le délai
// écoulé (et le relevé à jour), une tâche le signale aux responsables. Elle ne fait que lire
// (N0) : elle n'affecte ni ne rapproche aucune transaction. Fonctions pures.

export const SOURCES_VERIFICATION = ['banque', 'piece'] as const
export type SourceVerification = (typeof SOURCES_VERIFICATION)[number]

/** Jours accordés après la date limite avant de signaler l'absence. */
export const DELAI_VERIFICATION_DEFAUT = 5

/** Action de la tâche créée quand rien n'est arrivé : unique par échéance et par cycle. */
export const ACTION_VERIFICATION = 'verifier_reception'

const centimes = z.number().int().nonnegative()

/**
 * Ce qu'une règle vérifie (colonne « verification » de regles_echeance). Le tiers ou un mot du
 * libellé est exigé : sans eux, tout mouvement conviendrait.
 */
export const verificationRegle = z
  .object({
    source: z.enum(SOURCES_VERIFICATION),
    /** Banque : reçu (« encaisser », par défaut) ou débité (« payer »). */
    sens: z.enum(SENS_PAIEMENT).optional(),
    /** Banque : ses libellés bancaires ou son IBAN ; pièce : son émetteur. */
    tiersId: z.uuid().nullable().optional(),
    /** Mots attendus dans le libellé du relevé, ou dans le nom, l'émetteur lu ou l'objet de la pièce. */
    libelle: z.string().trim().min(2).max(80).nullable().optional(),
    /** Pièce : son type (facture, fiche de paie…). */
    typePiece: z.enum(TYPES_PIECE).nullable().optional(),
    /** Fourchette du montant (en valeur absolue) ; une borne seule suffit. */
    montantMinCts: centimes.nullable().optional(),
    montantMaxCts: centimes.nullable().optional(),
    /** Banque : le compte où il arrive ; à défaut, les comptes de l'entité. */
    compteId: z.uuid().nullable().optional(),
    delaiJours: z.number().int().min(0).max(60).optional(),
  })
  .refine((v) => Boolean(v.tiersId || v.libelle), {
    message: 'Préciser le tiers attendu ou un mot du libellé : sans eux, tout conviendrait.',
    path: ['libelle'],
  })
  .refine((v) => (v.montantMinCts ?? 0) <= (v.montantMaxCts ?? Number.MAX_SAFE_INTEGER), {
    message: 'Le montant minimum dépasse le maximum.',
    path: ['montantMaxCts'],
  })
  .refine((v) => v.source === 'banque' || (v.sens === undefined && !v.compteId), {
    message: 'Sens et compte ne valent que pour un mouvement bancaire.',
    path: ['sens'],
  })
  .refine((v) => v.source === 'piece' || !v.typePiece, {
    message: 'Le type de pièce ne vaut que pour une pièce reçue.',
    path: ['typePiece'],
  })
export type VerificationRegle = z.infer<typeof verificationRegle>

/**
 * Une vérification constate seule : ni paiement attendu (il se rapprocherait de la même ligne
 * du relevé), ni modèles de tâches (sa tâche ne naît que de l'absence).
 */
export function verificationIncompatible(r: {
  verification: VerificationRegle | null | undefined
  paiement: unknown
  taches: readonly unknown[]
}): string | null {
  if (!r.verification) return null
  if (r.paiement) return 'Une vérification ne porte pas de paiement attendu : retirer le paiement.'
  if (r.taches.length > 0)
    return 'Une vérification n’a pas de tâches : la sienne naît seulement si rien n’arrive.'
  return null
}

/** Texte comparable mot à mot : majuscules sans accents, ponctuation en espaces. */
function mots(texte: string): string {
  return ` ${normaliserLibelle(texte)
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()} `
}

/** Le texte contient-il ces mots, entiers et dans l'ordre (« CAF » ne trouve pas « CAFE ») ? */
function contientMots(texte: string, motif: string): boolean {
  const cible = mots(motif)
  return cible.trim() !== '' && mots(texte).includes(cible)
}

function dansFourchette(v: VerificationRegle, montantCts: number | null): boolean {
  const min = v.montantMinCts ?? null
  const max = v.montantMaxCts ?? null
  if (min === null && max === null) return true
  if (montantCts === null) return false
  const absolu = Math.abs(montantCts)
  return (min === null || absolu >= min) && (max === null || absolu <= max)
}

export type MouvementObserve = {
  id: string
  date: string
  /** Signé : négatif au débit. */
  montantCts: number
  libelleNormalise: string
  ibanContrepartie: string | null
  compteId: string
}

/** Ce qui désigne le tiers attendu sur un relevé. */
export type TiersObserve = { libellesBancaires: readonly string[]; iban: string | null }

export function mouvementCorrespond(
  v: VerificationRegle,
  tiers: TiersObserve | null,
  m: MouvementObserve,
): boolean {
  const sens = v.sens ?? 'encaisser'
  if (sens === 'encaisser' ? m.montantCts <= 0 : m.montantCts >= 0) return false
  if (v.compteId && m.compteId !== v.compteId) return false
  if (!dansFourchette(v, m.montantCts)) return false
  if (v.libelle && !contientMots(m.libelleNormalise, v.libelle)) return false
  if (v.tiersId) {
    if (!tiers) return false
    const iban = (x: string | null) => (x ?? '').replace(/\s/g, '').toUpperCase()
    const parIban = iban(tiers.iban) !== '' && iban(tiers.iban) === iban(m.ibanContrepartie)
    // Comme le rapprochement : un libellé bancaire du tiers contenu dans celui du relevé.
    const libelle = normaliserLibelle(m.libelleNormalise)
    const parLibelle = tiers.libellesBancaires.some(
      (l) => l.trim() !== '' && libelle.includes(normaliserLibelle(l)),
    )
    if (!parIban && !parLibelle) return false
  }
  return true
}

export type PieceObservee = {
  id: string
  /** Date du document, à défaut celle de son arrivée. */
  date: string
  emetteurId: string | null
  type: TypePiece
  montantCts: number | null
  /** Nom d'arrivée, nom de rangement, émetteur et objet lus. */
  textes: readonly (string | null)[]
}

export function pieceCorrespond(v: VerificationRegle, p: PieceObservee): boolean {
  if (v.tiersId && p.emetteurId !== v.tiersId) return false
  if (v.typePiece && p.type !== v.typePiece) return false
  if (!dansFourchette(v, p.montantCts)) return false
  if (v.libelle && !p.textes.some((t) => t && contientMots(t, v.libelle!))) return false
  return true
}

export type FenetreVerification = { du: string; limite: string; au: string }

/**
 * Fenêtre où la preuve d'une échéance est cherchée : du lendemain de la date limite de
 * l'occurrence précédente (faute de quoi, un mois avant) à sa date limite (fin de fenêtre,
 * sinon date, saisie à la main comprise) plus le délai. Les fenêtres voisines se chevauchent
 * du délai : un retard compte pour la période échue.
 */
export function fenetreVerification(
  regle: Regle,
  echeance: { dateTheorique: string; date: string; finFenetre: string | null },
  delaiJours: number,
  calendrier: CalendrierOuvre,
): FenetreVerification {
  const limite = echeance.finFenetre ?? echeance.date
  const precedentes = calculerEcheances(
    { ...regle, active: true },
    {
      du: ajouterJours(echeance.dateTheorique, -400),
      au: ajouterJours(echeance.dateTheorique, -1),
      calendrier,
    },
  )
  const precedente = precedentes.at(-1)
  const limitePrecedente = precedente
    ? (precedente.finFenetre ?? precedente.date)
    : ajouterMois(echeance.dateTheorique, -1)
  return { du: ajouterJours(limitePrecedente, 1), limite, au: ajouterJours(limite, delaiJours) }
}

export type EcheanceAVerifier = {
  id: string
  statut: StatutEcheance
  du: string
  au: string
  /** Preuve déjà retenue (transaction ou pièce) : elle ne sert plus à une autre échéance. */
  preuveId: string | null
}

export type DecisionVerification =
  | { echeanceId: string; decision: 'trouvee'; preuveId: string }
  | { echeanceId: string; decision: 'en_attente' | 'manquante' | 'releve_manquant' }

/**
 * Décision pour chaque échéance ouverte, la plus ancienne d'abord : la première preuve libre
 * de sa fenêtre, sinon attendre jusqu'à la fin de la fenêtre ; ensuite, « manquante » si le
 * relevé couvre cette fin (« sans_objet » : pièces, rien à couvrir), sinon « relevé manquant »
 * plutôt qu'une fausse alerte.
 */
export function deciderVerifications(entree: {
  echeances: readonly EcheanceAVerifier[]
  indices: readonly { id: string; date: string }[]
  aujourdhui: string
  couvertJusquAu: string | null | 'sans_objet'
}): DecisionVerification[] {
  const prises = new Set(entree.echeances.flatMap((e) => (e.preuveId ? [e.preuveId] : [])))
  const indices = [...entree.indices].sort(
    (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id),
  )
  const ouvertes = entree.echeances
    .filter((e) => e.statut === 'a_venir' || e.statut === 'en_preparation')
    .sort((a, b) => a.au.localeCompare(b.au) || a.du.localeCompare(b.du))
  return ouvertes.map((e): DecisionVerification => {
    const preuve = indices.find((i) => !prises.has(i.id) && i.date >= e.du && i.date <= e.au)
    if (preuve) {
      prises.add(preuve.id)
      return { echeanceId: e.id, decision: 'trouvee', preuveId: preuve.id }
    }
    if (entree.aujourdhui <= e.au) return { echeanceId: e.id, decision: 'en_attente' }
    const couvert =
      entree.couvertJusquAu === 'sans_objet' ||
      (entree.couvertJusquAu !== null && entree.couvertJusquAu >= e.au)
    return { echeanceId: e.id, decision: couvert ? 'manquante' : 'releve_manquant' }
  })
}

const TYPES_PIECE_LISIBLES: Record<TypePiece, string> = {
  facture: 'facture',
  avis: 'avis',
  courrier: 'courrier',
  accuse_depot: 'accusé de dépôt',
  releve: 'relevé',
  contrat: 'contrat',
  fiche_paie: 'fiche de paie',
  autre: 'autre',
}

/** « vérifie un encaissement de Salesforce « NOTE DE FRAIS », au moins 10,00 € » */
export function decrireVerification(v: VerificationRegle, nomTiers: string | null): string {
  const quoi =
    v.source === 'piece'
      ? `une pièce reçue${v.typePiece ? ` (${TYPES_PIECE_LISIBLES[v.typePiece]})` : ''}`
      : (v.sens ?? 'encaisser') === 'encaisser'
        ? 'un encaissement'
        : 'un débit'
  const min = v.montantMinCts ?? null
  const max = v.montantMaxCts ?? null
  const montant =
    min !== null && max !== null
      ? `entre ${formaterMontant(min)} et ${formaterMontant(max)}`
      : min !== null
        ? `au moins ${formaterMontant(min)}`
        : max !== null
          ? `au plus ${formaterMontant(max)}`
          : null
  const delai =
    v.delaiJours !== undefined && v.delaiJours !== DELAI_VERIFICATION_DEFAUT
      ? `${v.delaiJours} jours de délai`
      : null
  const tete = [
    `vérifie ${quoi}`,
    v.tiersId && nomTiers ? `de ${nomTiers}` : null,
    v.libelle ? `« ${v.libelle} »` : null,
  ]
    .filter(Boolean)
    .join(' ')
  return [tete, montant, delai].filter(Boolean).join(', ')
}
